import { isEqual, uniq } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { EntityDetailSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  MatchableKind,
  SuggestableKind,
} from '@/modules/aiSuggestions/aiSuggestions.types';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { getSuggestionEvidenceDocumentIds } from '@/modules/aiSuggestions/evidence';
import {
  processSuggestionsDataV2,
  useFetchEntityAISuggestions,
} from '@/modules/aiSuggestions/hooks/useFetchEntityAISuggestions';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getAISuggestionsForEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { AiSuggestionKind } from '@/types/schema';
import { debug } from '@/utils/diagnostics';

import { EntitySuggestionsContext } from './EntitySuggestions.context';

function useContextValue(): EntitySuggestionsContext {
  debug('[DEBUG] EntitySuggestions - useContextValue called');

  const { entityId, entityType } = useEntityDetailsContext();
  // We only want to see suggestions if the user can edit the household.
  const shouldSeeSuggestions = useCanEditHouseholdData();
  const { initPreviouslyAcknowledgedSuggestions, getAcknowledgedSuggestion } =
    useSuggestionAcknowledgementContext();

  // This includes suggestions that have been acknowledged and unacknowledged.
  // We want to know the acknowledged suggestions so that we can still show
  // the evidence for the suggestion (if accepted).
  const [suggestionsByKind, setSuggestionsByKind] = useState<
    Record<AiSuggestionKind, EntityDetailSuggestion[]>
  >({} as Record<AiSuggestionKind, EntityDetailSuggestion[]>);

  // Add a ref to track previous suggestions
  const previousSuggestionsRef = useRef<EntityDetailSuggestion[]>([]);

  const { refetch, loading } = useFetchEntityAISuggestions({
    entityId,
    skip: !entityId || !shouldSeeSuggestions,

    onCompleted: (data) => {
      debug('[DEBUG] EntitySuggestions - data.aiSuggestions completed', {
        entityId,
      });

      const { suggestions, groupedByKind } = processSuggestionsDataV2(data);

      debug('[DEBUG] EntitySuggestions - processing suggestions', {
        count: suggestions.length,
        entityId,
      });

      // Check if suggestions have actually changed to prevent unnecessary updates
      const suggestionsChanged = !isEqual(
        previousSuggestionsRef.current,
        suggestions
      );

      if (!suggestionsChanged) {
        debug(
          '[DEBUG] EntitySuggestions - suggestions unchanged, skipping update',
          {
            entityId,
          }
        );
        return;
      }

      // Update the ref with the current suggestions
      previousSuggestionsRef.current = [...suggestions];

      // Only initialize previously acknowledged suggestions if data has changed
      initPreviouslyAcknowledgedSuggestions(suggestions);

      debug('[DEBUG] EntitySuggestions - setting suggestionsByKind', {
        kinds: Object.keys(groupedByKind).length,
        entityId,
        groupedByKind,
      });

      setSuggestionsByKind(groupedByKind);
    },
  });

  const getCurrentSectionSuggestions = useCallback(
    (currentSection: EditEntitySection) => {
      debug('[DEBUG] EntitySuggestions - getCurrentSectionSuggestions called', {
        currentSection,
        entityId,
      });

      return getAISuggestionsForEntitySection(
        entityType,
        currentSection,
        suggestionsByKind
      );
    },
    [entityType, suggestionsByKind, entityId]
  );

  const getSuggestableSuggestion = useCallback(
    (kind: SuggestableKind) => {
      debug('[DEBUG] EntitySuggestions - getSuggestableSuggestion called', {
        kind,
        entityId,
        hasSuggestion: !!suggestionsByKind[kind]?.[0],
      });

      return suggestionsByKind[kind]?.[0];
    },
    [suggestionsByKind, entityId]
  );

  const getMatchableSuggestion = useCallback(
    (kind: MatchableKind, matchedId: string) => {
      debug('[DEBUG] EntitySuggestions - getMatchableSuggestion called', {
        kind,
        matchedId,
        entityId,
      });

      const matched = getAcknowledgedSuggestion(matchedId);

      const byKind = suggestionsByKind[kind];

      return (byKind || []).find((b) =>
        matched.some((m) => b.id === m.suggestionID)
      );
    },
    [getAcknowledgedSuggestion, suggestionsByKind, entityId]
  );

  // Track state changes
  useEffect(() => {
    debug('[DEBUG] EntitySuggestions - suggestionsByKind changed', {
      kinds: Object.keys(suggestionsByKind).length,
      entityId,
    });
  }, [suggestionsByKind, entityId]);

  // Suggestion evidence links to file paragraphs.
  const suggestionDocumentIds = useMemo(() => {
    const allSuggestions = Object.values(suggestionsByKind).flat();
    return uniq(
      allSuggestions.flatMap((suggestion) =>
        getSuggestionEvidenceDocumentIds(suggestion.evidence)
      )
    );
  }, [suggestionsByKind]);

  return useMemo(
    () => ({
      suggestionsByKind,
      getCurrentSectionSuggestions,
      getSuggestableSuggestion,
      getMatchableSuggestion,
      refetchSuggestions: refetch,
      loading,
      suggestionDocumentIds,
    }),
    [
      suggestionsByKind,
      getCurrentSectionSuggestions,
      getSuggestableSuggestion,
      getMatchableSuggestion,
      refetch,
      loading,
      suggestionDocumentIds,
    ]
  );
}

export function EntitySuggestionsProvider({ children }: PropsWithChildren) {
  debug('[DEBUG] EntitySuggestionsProvider - rendering');
  const value = useContextValue();

  return (
    <EntitySuggestionsContext.Provider value={value}>
      {children}
    </EntitySuggestionsContext.Provider>
  );
}
